/* ============================================================================
   Ancona Pass 25 — metrics bridge (ancona-metrics.js)  [B9 + I8]
   Purpose: (1) initialize window.dataLayer BEFORE the ancona layers run, so
   every existing track() stub (they all guard on window.dataLayer) finally
   lands its KPI events; (2) republish the layers' public `ancona:*`
   CustomEvents to Shopify's web-pixel bus via Shopify.analytics.publish when
   it exists (graceful no-op otherwise); (3) tabsync — keep the header Saved
   hub fresh across browser tabs by translating cross-tab `storage` events on
   the shared Saved store into the `ancona:saved_add` CustomEvent the frozen
   launch.js hub listener already refreshes on.
   Load position contract: this <script> must be linked in layout/theme.liquid
   BEFORE ancona-enhance.js (and therefore before every other ancona layer) so
   the dataLayer init precedes the layers' first track() calls.
   Feature flags: this <script> tag's data-ancona-metrics attribute (tokens:
   datalayer | publish | tabsync). Absent attribute -> all; empty string ->
   none. No PII, no cookies, no storage writes, no third-party code.
   Revert: remove the two data-ancona-metrics lines in layout/theme.liquid +
   this file.
   ============================================================================ */
(function () {
  'use strict';
  try {
    if (window.__anconaMetrics) return;
    window.__anconaMetrics = true;

    var script = document.currentScript;
    var featAttr = script ? script.getAttribute('data-ancona-metrics') : null;
    /* Absent attribute → all features; explicit empty string → none (documented revert path). */
    var FEATURES = (featAttr == null ? 'datalayer,publish,tabsync' : featAttr).split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    function on(f) { return FEATURES.indexOf(f) !== -1; }

    /* ---- B9a: dataLayer init (must run before any layer's track()) --------- */
    if (on('datalayer')) {
      try { window.dataLayer = window.dataLayer || []; } catch (e) { /* never break the page */ }
    }

    /* ---- B9b: republish ancona:* CustomEvents to the Shopify pixel bus ------
       CustomEvents cannot be wildcard-listened, so the public event vocabulary
       is enumerated here. This list covers every `track('…')` call currently
       emitted by the ancona layers and snippets (verified by grep on this
       theme) plus the Pass 25 layers. Honest coverage note: if a future pass
       adds a new event name, it must be appended here to reach the pixel bus
       (it still reaches window.dataLayer without any change). */
    var EVENTS = [
      /* Pass 12 innovation.js */
      'saved_add', 'saved_remove', 'shortlist_share', 'return_to_results',
      'recently_viewed_clear', 'guide_toc_click',
      /* Pass 13 launch.js */
      'saved_open', 'compare_add', 'compare_remove', 'compare_open',
      /* Pass 16 deluxe.js */
      'inquiry_start', 'inquiry_context',
      /* Pass 17 print.js */
      'product_print', 'guide_print', 'guide_share',
      /* enhance.js */
      'sticky_add_to_cart', 'cta_click', 'recs_fallback_shown',
      /* Pass 25 layers (saved-cart / freshen / shortlist successor / variant) */
      'saved_cart_add', 'saved_refresh', 'shortlist_view', 'saved_update'
    ];

    if (on('publish')) {
      var republish = function (name, e) {
        try {
          var detail = (e && e.detail) ? e.detail : {};
          /* Cross-tab mirror events (see tabsync below) are UI refresh signals,
             not new shopper actions — never count them as KPI events. */
          if (detail && detail.source === 'tabsync') return;
          /* Graceful no-op when the pixel sandbox is not present. */
          if (!(window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function')) return;
          window.Shopify.analytics.publish('ancona:' + name, detail);
        } catch (err) { /* analytics must never break shopping */ }
      };
      EVENTS.forEach(function (name) {
        document.addEventListener('ancona:' + name, function (e) { republish(name, e); });
      });
    }

    /* ---- I8: tabsync — cross-tab Saved-hub freshness -----------------------
       The `storage` event fires only in OTHER tabs when the shared Saved store
       (ancona:v1:saved) changes. Dispatching `ancona:saved_add` here re-runs
       the frozen launch.js document listener (refreshHub), which recomputes
       BOTH header hub counts (saved and compare) from storage.
       Honest partial coverage (fixing these would require editing the frozen
       files, which is off-limits): an already-open Saved/Compare dialog's rows,
       the PDP Save button's aria-pressed state, and changes keyed to the
       compare store (ancona:v1:compare) are NOT refreshed by this bridge.
       detail.source === 'tabsync' marks the event as a mirror so the publish
       bridge above (and the signature pulse layer) can ignore it. */
    if (on('tabsync')) {
      window.addEventListener('storage', function (e) {
        try {
          if (!e || e.key !== 'ancona:v1:saved') return;
          document.dispatchEvent(new CustomEvent('ancona:saved_add', { detail: { source: 'tabsync' } }));
        } catch (err) { /* never break the page */ }
      });
    }
  } catch (e) { /* the metrics bridge must never break the page */ }
})();
